/* ============================================================================
   POPUP EDIT WORKFLOW (admin) — page /workflow · 26 Okt 2026
   ============================================================================
   Cipta / ubah / padam satu card workflow. Simpan = services/galeriWorkflowService.js (transaksi
   pada config/workflow_galeri). Semakan nilai = utils/galeriWorkflow.js normalWorkflow — gagal
   dipapar MERAH dalam popup (tidak senyap). Admin UI = English. Gaya modal v3.9 sedia ada (.s-modal).
   Padam = dua langkah (Delete → Confirm delete) supaya satu klik silap tidak cukup.
   ========================================================================== */
import { useState } from 'react';
import { X, Trash2 } from 'lucide-react';
import { Btn, Segmented } from '../design/ui';
import GambarAliranWf from './GambarAliranWf';
import { KATEGORI_WF, HASIL_WF, ILUSTRASI_WF, HAD_WF, normalWorkflow } from '../utils/galeriWorkflow';
import { simpanWorkflow, padamWorkflow } from '../services/galeriWorkflowService';
import { ROBOT_WF, robotWorkflow } from '../utils/workflowRobot';

const Label = ({ children, hint }) => (
    <div className="wfg-lbl">
        <span>{children}</span>
        {hint && <span className="wfg-lbl__hint">{hint}</span>}
    </div>
);

export default function WorkflowEditModal({ awal, baru, onTutup }) {
    /* `robot` diisi dengan nilai BERKESAN (medan sendiri atau lalai ikut id) supaya admin nampak sambungan sebenar. */
    const [f, setF] = useState(() => ({ ...awal, tag: (awal.tag || []).join(', '), robot: robotWorkflow(awal) }));
    const [sedang, setSedang] = useState(false);
    const [ralat, setRalat] = useState('');
    const [sahPadam, setSahPadam] = useState(false);
    const ubah = (medan, nilai) => { setF((x) => ({ ...x, [medan]: nilai })); setRalat(''); };

    const tutup = () => { if (!sedang) onTutup(); };

    const simpan = async () => {
        const n = normalWorkflow(f);
        if (!n.ok) { setRalat(n.ralat); return; }
        setSedang(true);
        setRalat('');
        try {
            await simpanWorkflow(n.wf);
            onTutup();
        } catch (e) {
            setRalat('Save failed: ' + (e?.message || e));
            setSedang(false);
        }
    };

    const padam = async () => {
        if (!sahPadam) { setSahPadam(true); return; }
        setSedang(true);
        setRalat('');
        try {
            await padamWorkflow(f.id);
            onTutup();
        } catch (e) {
            setRalat('Delete failed: ' + (e?.message || e));
            setSedang(false);
            setSahPadam(false);
        }
    };

    const togolHasil = (id) => {
        const ada = (f.hasil || []).includes(id);
        ubah('hasil', ada ? f.hasil.filter((h) => h !== id) : [...(f.hasil || []), id]);
    };

    return (
        <div className="s-root s-modal" role="dialog" aria-modal="true" aria-label={baru ? 'Create workflow' : 'Edit workflow'} onClick={tutup}>
            <div className="s-modal__panel" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
                <header className="s-modal__head">
                    <h2 style={{ flex: 1, margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--s-ink)' }}>
                        {baru ? 'Create workflow' : 'Edit workflow'}
                    </h2>
                    <button type="button" className="s-modal__x" aria-label="Close" onClick={tutup}>
                        <X size={15} strokeWidth={2} />
                    </button>
                </header>

                <div className="s-modal__body wfg-borang">
                    <div>
                        <Label hint={`${f.nama.length}/${HAD_WF.nama}`}>Name</Label>
                        <input className="s-input" value={f.nama} maxLength={HAD_WF.nama} autoFocus
                            placeholder="One image, many scenes" onChange={(e) => ubah('nama', e.target.value)} />
                    </div>

                    <div>
                        <Label hint={`${f.penerangan.length}/${HAD_WF.penerangan}`}>Description</Label>
                        <textarea className="s-input wfg-textarea" rows={2} value={f.penerangan} maxLength={HAD_WF.penerangan}
                            placeholder="Same product in every scene." onChange={(e) => ubah('penerangan', e.target.value)} />
                    </div>

                    <div className="wfg-borang__dua">
                        <div>
                            <Label>Status</Label>
                            <Segmented value={f.status} onChange={(v) => ubah('status', v)}
                                options={[{ value: 'published', label: 'Published' }, { value: 'draft', label: 'Draft' }]} />
                        </div>
                        <div>
                            <Label>Category</Label>
                            <select className="s-select" value={f.kategori} onChange={(e) => ubah('kategori', e.target.value)}>
                                {KATEGORI_WF.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* ★ [3 Nov] Sambungan ke robot — dropdown Workflow Control Panel (v4 mod New) hanya
                        memapar workflow yang ada robot. Not linked = tidak muncul kepada sesiapa. */}
                    <div>
                        <Label hint="Shows in the Control Panel dropdown only when linked">Robot</Label>
                        <select className="s-select" value={f.robot || ''} onChange={(e) => ubah('robot', e.target.value)}>
                            <option value="">Not linked</option>
                            {ROBOT_WF.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                        </select>
                    </div>

                    <div>
                        <Label>Card picture</Label>
                        <div className="wfg-pilihgambar">
                            {ILUSTRASI_WF.map((i) => (
                                <button key={i.id} type="button" title={i.label} aria-pressed={f.ilustrasi === i.id}
                                    className={'wfg-pilihgambar__item' + (f.ilustrasi === i.id ? ' is-on' : '')}
                                    onClick={() => ubah('ilustrasi', i.id)}>
                                    <GambarAliranWf ilustrasi={i.id} saiz={16} className="wfg-art wfg-art--kecil" />
                                    <span>{i.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <Label hint={`Comma separated · max ${HAD_WF.bilTag}`}>Tags</Label>
                        <input className="s-input" value={f.tag} placeholder="1–5 scenes, Saves credits"
                            onChange={(e) => ubah('tag', e.target.value)} />
                    </div>

                    <div className="wfg-borang__dua">
                        <div>
                            <Label>Output goes to</Label>
                            <div className="wfg-hasilpilih">
                                {HASIL_WF.map((h) => (
                                    <button key={h.id} type="button" aria-pressed={(f.hasil || []).includes(h.id)}
                                        className={'wfg-pill' + ((f.hasil || []).includes(h.id) ? ' is-on' : '')}
                                        onClick={() => togolHasil(h.id)}>
                                        {h.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label hint="Smaller = first">Order</Label>
                            <input className="s-input" type="number" min={0} max={HAD_WF.susunan} step={1} value={f.susunan}
                                onChange={(e) => ubah('susunan', e.target.value)} />
                        </div>
                    </div>

                </div>

                {/* Di LUAR badan yang menatal — mesej mesti kelihatan tanpa scroll (diuji 26 Okt). */}
                {ralat && <div className="wfg-ralat wfg-ralat--modal" role="alert">{ralat}</div>}

                <footer className="s-modal__foot" style={{ justifyContent: 'space-between' }}>
                    <div>
                        {!baru && (
                            <Btn variant={sahPadam ? 'primary' : 'quiet'} size="sm" disabled={sedang} onClick={padam}
                                className={sahPadam ? 'wfg-padam-sah' : 'wfg-padam'} icon={<Trash2 size={14} />}>
                                {sahPadam ? 'Confirm delete' : 'Delete'}
                            </Btn>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Btn variant="ghost" size="sm" disabled={sedang} onClick={tutup}>Cancel</Btn>
                        <Btn size="sm" disabled={sedang} onClick={simpan}>{sedang ? 'Saving…' : (baru ? 'Create' : 'Save')}</Btn>
                    </div>
                </footer>
            </div>
        </div>
    );
}
